import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StaleDiaryItem {
  claim_id: string;
  fh_claim_num: string;
  tier_claim_num: string;
  diary_date: string;
  diary_type: string;
  description: string;
  handler_name: string;
  days_overdue: number;
}

interface AgedClaimItem {
  claim_id: string;
  fh_claim_num: string;
  tier_claim_num: string;
  loss_date: string;
  age_days: number;
  total_incurred: number;
  handler_name: string;
  company_name: string;
}

interface InactiveClaimItem {
  claim_id: string;
  fh_claim_num: string;
  tier_claim_num: string;
  loss_date: string;
  total_incurred: number;
  handler_name: string;
  company_name: string;
  last_transaction: string;
  last_diary: string;
}

interface ReserveAlertItem {
  claim_id: string;
  fh_claim_num: string;
  tier_claim_num: string;
  total_incurred: number;
  handler_name: string;
  company_name: string;
  trans_date: string;
  total_amount: number;
  trans_desc: string;
}

interface HealthSection<T> {
  count: number;
  items: T[];
}

interface ClaimsHealthResponse {
  stale_diaries: HealthSection<StaleDiaryItem>;
  aged_claims: HealthSection<AgedClaimItem>;
  inactive_claims: HealthSection<InactiveClaimItem>;
  reserve_alerts: HealthSection<ReserveAlertItem>;
  summary: {
    total_issues: number;
    stale_diary_count: number;
    aged_claim_count: number;
    inactive_claim_count: number;
    reserve_alert_count: number;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

// ─── Summary Stat Card ────────────────────────────────────────────────────────

interface SummaryCardProps {
  label: string;
  count: number;
  color: string;
  icon: React.ReactNode;
}

function SummaryCard({ label, count, color, icon }: SummaryCardProps) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4 flex items-center gap-4 min-w-0">
      <div
        className="flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}1a` }}
      >
        <span style={{ color }}>{icon}</span>
      </div>
      <div className="min-w-0">
        <p
          className="text-2xl font-bold tabular-nums"
          style={{ color }}
        >
          {count.toLocaleString()}
        </p>
        <p className="text-sm font-medium text-slate-500 truncate">{label}</p>
      </div>
    </div>
  );
}

// ─── Alert Section Wrapper ────────────────────────────────────────────────────

interface AlertSectionProps {
  title: string;
  count: number;
  badgeColor: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  totalItems: number;
  expanded: boolean;
  onToggleExpand: () => void;
}

function AlertSection({
  title,
  count,
  badgeColor,
  icon,
  children,
  totalItems,
  expanded,
  onToggleExpand,
}: AlertSectionProps) {
  if (count === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
        <div className="flex items-center gap-3">
          <span className="text-slate-500">{icon}</span>
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          <span
            className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: badgeColor }}
          >
            {count}
          </span>
        </div>
        {totalItems > 5 && (
          <button
            onClick={onToggleExpand}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            {expanded ? 'Show less' : `Show all ${totalItems}`}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {children}
      </div>
    </div>
  );
}

// ─── Health Monitor ───────────────────────────────────────────────────────────

const PREVIEW_COUNT = 5;

export default function HealthMonitor() {
  const navigate = useNavigate();

  const [data, setData] = useState<ClaimsHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Expand state per section
  const [staleDiariesExpanded, setStaleDiariesExpanded] = useState(false);
  const [agedClaimsExpanded, setAgedClaimsExpanded] = useState(false);
  const [inactiveClaimsExpanded, setInactiveClaimsExpanded] = useState(false);
  const [reserveAlertsExpanded, setReserveAlertsExpanded] = useState(false);

  const fetchHealth = async () => {
    setLoading(true);
    setError(null);
    try {
      const result: ClaimsHealthResponse = await api.getClaimsHealth();
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load claims health data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Page title */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Claims Health Monitor</h1>
          <p className="text-sm text-slate-500 mt-0.5">Alerts & Action Items</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-red-200 shadow-sm px-6 py-10 flex flex-col items-center gap-4">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-slate-700 font-medium">{error}</p>
            <button
              onClick={fetchHealth}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        ) : data ? (
          <>
            {/* ── Summary Banner ──────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <SummaryCard
                label="Total Issues"
                count={data.summary.total_issues}
                color="#3b82f6"
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
              />
              <SummaryCard
                label="Stale Diaries"
                count={data.summary.stale_diary_count}
                color="#ef4444"
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
              />
              <SummaryCard
                label="Aged Claims"
                count={data.summary.aged_claim_count}
                color="#f59e0b"
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
              <SummaryCard
                label="Inactive Claims"
                count={data.summary.inactive_claim_count}
                color="#8b5cf6"
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                }
              />
              <SummaryCard
                label="Reserve Alerts"
                count={data.summary.reserve_alert_count}
                color="#ec4899"
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                }
              />
            </div>

            {/* ── Stale Diaries ───────────────────────────────────────────── */}
            {(() => {
              const items = staleDiariesExpanded
                ? data.stale_diaries.items
                : data.stale_diaries.items.slice(0, PREVIEW_COUNT);
              return (
                <AlertSection
                  title="Overdue Diary Entries"
                  count={data.stale_diaries.count}
                  badgeColor="#ef4444"
                  totalItems={data.stale_diaries.items.length}
                  expanded={staleDiariesExpanded}
                  onToggleExpand={() => setStaleDiariesExpanded((v) => !v)}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  }
                >
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">TCM #</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Diary Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Type</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600">Description</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Handler</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Days Overdue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-slate-400">No stale diaries.</td>
                        </tr>
                      ) : (
                        items.map((item, idx) => {
                          const overdueCls =
                            item.days_overdue > 90
                              ? 'text-red-600 font-semibold'
                              : item.days_overdue > 30
                              ? 'text-amber-600 font-semibold'
                              : 'text-slate-700';
                          return (
                            <tr
                              key={`${item.claim_id}-${idx}`}
                              className={[
                                'border-b border-slate-100',
                                idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60',
                              ].join(' ')}
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={() => navigate(`/claims/${item.claim_id}`)}
                                  className="font-mono text-blue-700 font-medium hover:underline focus:outline-none"
                                >
                                  {item.tier_claim_num || item.fh_claim_num || item.claim_id}
                                </button>
                              </td>
                              <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(item.diary_date)}</td>
                              <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{item.diary_type || '—'}</td>
                              <td className="px-4 py-3 text-slate-700 max-w-[260px] truncate">{item.description || '—'}</td>
                              <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{item.handler_name || '—'}</td>
                              <td className={`px-4 py-3 text-right tabular-nums whitespace-nowrap ${overdueCls}`}>
                                {item.days_overdue.toLocaleString()}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </AlertSection>
              );
            })()}

            {/* ── Aged Claims ─────────────────────────────────────────────── */}
            {(() => {
              const items = agedClaimsExpanded
                ? data.aged_claims.items
                : data.aged_claims.items.slice(0, PREVIEW_COUNT);
              return (
                <AlertSection
                  title="Aged Claims (>2 Years)"
                  count={data.aged_claims.count}
                  badgeColor="#f59e0b"
                  totalItems={data.aged_claims.items.length}
                  expanded={agedClaimsExpanded}
                  onToggleExpand={() => setAgedClaimsExpanded((v) => !v)}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                >
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">TCM #</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Loss Date</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Age (days)</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Coverholder</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Handler</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Total Incurred</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-slate-400">No aged claims.</td>
                        </tr>
                      ) : (
                        items.map((item, idx) => {
                          const ageCls =
                            item.age_days > 1000
                              ? 'text-red-600 font-semibold'
                              : item.age_days > 730
                              ? 'text-amber-600 font-semibold'
                              : 'text-slate-700';
                          return (
                            <tr
                              key={`${item.claim_id}-${idx}`}
                              className={[
                                'border-b border-slate-100',
                                idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60',
                              ].join(' ')}
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={() => navigate(`/claims/${item.claim_id}`)}
                                  className="font-mono text-blue-700 font-medium hover:underline focus:outline-none"
                                >
                                  {item.tier_claim_num || item.fh_claim_num || item.claim_id}
                                </button>
                              </td>
                              <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(item.loss_date)}</td>
                              <td className={`px-4 py-3 text-right tabular-nums whitespace-nowrap ${ageCls}`}>
                                {item.age_days.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{item.company_name || '—'}</td>
                              <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{item.handler_name || '—'}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-slate-800 font-medium whitespace-nowrap">
                                {formatCurrency(item.total_incurred)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </AlertSection>
              );
            })()}

            {/* ── Inactive Claims ──────────────────────────────────────────── */}
            {(() => {
              const items = inactiveClaimsExpanded
                ? data.inactive_claims.items
                : data.inactive_claims.items.slice(0, PREVIEW_COUNT);
              return (
                <AlertSection
                  title="Inactive Claims (90+ Days)"
                  count={data.inactive_claims.count}
                  badgeColor="#8b5cf6"
                  totalItems={data.inactive_claims.items.length}
                  expanded={inactiveClaimsExpanded}
                  onToggleExpand={() => setInactiveClaimsExpanded((v) => !v)}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  }
                >
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">TCM #</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Loss Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Coverholder</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Handler</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Last Transaction</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Last Diary</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Total Incurred</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-slate-400">No inactive claims.</td>
                        </tr>
                      ) : (
                        items.map((item, idx) => (
                          <tr
                            key={`${item.claim_id}-${idx}`}
                            className={[
                              'border-b border-slate-100',
                              idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60',
                            ].join(' ')}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <button
                                onClick={() => navigate(`/claims/${item.claim_id}`)}
                                className="font-mono text-blue-700 font-medium hover:underline focus:outline-none"
                              >
                                {item.tier_claim_num || item.fh_claim_num || item.claim_id}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(item.loss_date)}</td>
                            <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{item.company_name || '—'}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{item.handler_name || '—'}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(item.last_transaction)}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(item.last_diary)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-800 font-medium whitespace-nowrap">
                              {formatCurrency(item.total_incurred)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </AlertSection>
              );
            })()}

            {/* ── Reserve Alerts ───────────────────────────────────────────── */}
            {(() => {
              const items = reserveAlertsExpanded
                ? data.reserve_alerts.items
                : data.reserve_alerts.items.slice(0, PREVIEW_COUNT);
              return (
                <AlertSection
                  title="Significant Reserve Changes"
                  count={data.reserve_alerts.count}
                  badgeColor="#ec4899"
                  totalItems={data.reserve_alerts.items.length}
                  expanded={reserveAlertsExpanded}
                  onToggleExpand={() => setReserveAlertsExpanded((v) => !v)}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  }
                >
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">TCM #</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Coverholder</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Handler</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 whitespace-nowrap">Transaction Date</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Amount</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-600 whitespace-nowrap">Total Incurred</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-slate-400">No reserve alerts.</td>
                        </tr>
                      ) : (
                        items.map((item, idx) => (
                          <tr
                            key={`${item.claim_id}-${idx}`}
                            className={[
                              'border-b border-slate-100',
                              idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60',
                            ].join(' ')}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <button
                                onClick={() => navigate(`/claims/${item.claim_id}`)}
                                className="font-mono text-blue-700 font-medium hover:underline focus:outline-none"
                              >
                                {item.tier_claim_num || item.fh_claim_num || item.claim_id}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{item.company_name || '—'}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{item.handler_name || '—'}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(item.trans_date)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-800 font-medium whitespace-nowrap">
                              {formatCurrency(item.total_amount)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-800 font-medium whitespace-nowrap">
                              {formatCurrency(item.total_incurred)}
                            </td>
                            <td className="px-4 py-3 text-slate-700 max-w-[220px] truncate">{item.trans_desc || '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </AlertSection>
              );
            })()}
          </>
        ) : null}
      </div>
    </div>
  );
}
